import { AxiosResponse } from 'axios';
import { FieldType } from 'src/entity/ProjectSettingsForm/types';
import { ApiProjects } from 'src/shared/api/ApiProject';
import { ApiProjects2 } from 'src/shared/api/ApiProject2';
import { FormType, ProjectType } from 'src/shared/store/projects/types';

import {
  AddColumnFormParams,
  BulkCreateFormsParams,
  BulkCreateFormsPreviewRes,
  BulkCreateFormsRes,
  CreateFormParams,
  CreateFormRes,
  CreateProjectParams,
  CreateProjectRes,
  DirectoryAttributePayload,
  DirectoryHierarchyPayload,
  GetDirectoryValueWorkflowRes,
  GetDirectoryWorkflowHistoryParams,
  GetDirectoryWorkflowHistoryRes,
  GetDirectoryWorkflowRes,
  GetFormsByProjectIdParams,
  GetFormWorkflowHistoryParams,
  GetFormWorkflowHistoryRes,
  GetFormWorkflowRes,
  GetProjectsSearchParams,
  GetTableSettingsByFormIdParams,
  ListDirectoryAttributesRes,
  ListDirectoryHierarchyRes,
  ListProjectDirectoriesRes,
  RemoveColumnFormParams,
  TransitionDirectoryWorkflowParams,
  TransitionDirectoryWorkflowRes,
  TransitionFormWorkflowParams,
  TransitionFormWorkflowRes,
  UpdateColumnFormParams,
  UpdateDirectoryAttributePayload,
  UpdateDirectoryHierarchyPayload,
  UpdateFormParams,
  UpsertDirectoryWorkflowParams,
  UpsertFormDirectoryBindingPayload,
  UpsertFormWorkflowParams,
} from './types';

const getProjects = () => ApiProjects.get<void, Promise<AxiosResponse<ProjectType[]>>>(`/projects`);

const getProjectsWithSearch = (params: string | GetProjectsSearchParams) => {
  const query = new URLSearchParams();

  if (typeof params === 'string') {
    if (params.trim()) {
      query.set('search', params.trim());
    }
  } else {
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        query.set(key, String(value).trim());
      }
    });
  }

  const queryString = query.toString();
  return ApiProjects.get<void, Promise<AxiosResponse<ProjectType[]>>>(
    `/projects/search${queryString ? `?${queryString}` : ''}`,
  );
};

const getFormsByProjectId = (params: GetFormsByProjectIdParams) =>
  ApiProjects.get<GetFormsByProjectIdParams, Promise<AxiosResponse<FormType[]>>>(
    `/forms/${params.projectId}`,
  );

const getTableSettings = (params: GetTableSettingsByFormIdParams) =>
  ApiProjects.get<GetTableSettingsByFormIdParams, Promise<AxiosResponse<CreateFormRes>>>(
    `/table/fields/${params.formId}`,
  );

const createProject = (params: CreateProjectParams) =>
  ApiProjects2.post<string, Promise<AxiosResponse<CreateProjectRes>>, CreateProjectParams>(
    `/project`,
    params,
  );

const createForm = (params: CreateFormParams) =>
  ApiProjects2.post<string, Promise<AxiosResponse<CreateFormRes>>, CreateFormParams>(
    `/form`,
    params,
  );

const createFormsBulk = (params: BulkCreateFormsParams) =>
  ApiProjects2.post<string, Promise<AxiosResponse<BulkCreateFormsRes>>, BulkCreateFormsParams>(
    `/form/bulk`,
    params,
  );

const previewFormsBulk = (params: BulkCreateFormsParams) =>
  ApiProjects2.post<
    string,
    Promise<AxiosResponse<BulkCreateFormsPreviewRes>>,
    BulkCreateFormsParams
  >(`/form/bulk/preview`, params);

const updateForm = (params: UpdateFormParams) =>
  ApiProjects2.put<string, Promise<AxiosResponse<CreateFormRes>>, Omit<UpdateFormParams, 'id'>>(
    `/form/${params.id}`,
    {
      formDescription: params.formDescription,
      formName: params.formName,
      appearanceSettings: params.appearanceSettings,
      viewSettings: params.viewSettings,
      businessRules: params.businessRules,
      flkRules: params.flkRules,
      workflowSettings: params.workflowSettings,
      currentStatus: params.currentStatus,
    },
  );

const addColumnForm = (params: AddColumnFormParams) =>
  ApiProjects2.post<string, Promise<AxiosResponse<CreateFormRes>>, FieldType[]>(
    `/form/${params.id}/addColumn`,
    params.columns,
  );

const deleteColumnForm = (params: RemoveColumnFormParams) =>
  ApiProjects2.delete<string, Promise<AxiosResponse<CreateFormRes>>, FieldType[]>(
    `/form/${params.id}/deleteColumns`,
    {
      data: params.columns,
    },
  );

const updateColumnForm = (params: UpdateColumnFormParams) =>
  ApiProjects2.put<string, Promise<AxiosResponse<CreateFormRes>>, FieldType[]>(
    `/form/${params.id}/updateColumn`,
    params.columns,
  );

const getFormWorkflow = (formId: number) =>
  ApiProjects2.get<void, Promise<AxiosResponse<GetFormWorkflowRes>>>(`/form/${formId}/workflow`);

const upsertFormWorkflow = (formId: number, params: UpsertFormWorkflowParams) =>
  ApiProjects2.put<string, Promise<AxiosResponse<GetFormWorkflowRes>>, UpsertFormWorkflowParams>(
    `/form/${formId}/workflow`,
    params,
  );

const transitionFormWorkflow = (formId: number, params: TransitionFormWorkflowParams) =>
  ApiProjects2.post<
    string,
    Promise<AxiosResponse<TransitionFormWorkflowRes>>,
    TransitionFormWorkflowParams
  >(`/form/${formId}/workflow/transition`, params);

const getFormWorkflowHistory = (formId: number, params?: GetFormWorkflowHistoryParams) => {
  const query = new URLSearchParams();
  if (typeof params?.limit === 'number') {
    query.set('limit', String(params.limit));
  }
  if (typeof params?.offset === 'number') {
    query.set('offset', String(params.offset));
  }
  const queryString = query.toString();
  return ApiProjects2.get<void, Promise<AxiosResponse<GetFormWorkflowHistoryRes>>>(
    `/form/${formId}/workflow/history${queryString ? `?${queryString}` : ''}`,
  );
};

const getDirectoryWorkflow = (directoryId: number) =>
  ApiProjects2.get<void, Promise<AxiosResponse<GetDirectoryWorkflowRes>>>(
    `/directory/${directoryId}/workflow`,
  );

const upsertDirectoryWorkflow = (directoryId: number, params: UpsertDirectoryWorkflowParams) =>
  ApiProjects2.put<
    string,
    Promise<AxiosResponse<GetDirectoryWorkflowRes>>,
    UpsertDirectoryWorkflowParams
  >(`/directory/${directoryId}/workflow`, params);

const getDirectoryValueWorkflow = (directoryId: number, valueId: number) =>
  ApiProjects2.get<void, Promise<AxiosResponse<GetDirectoryValueWorkflowRes>>>(
    `/directory/${directoryId}/value/${valueId}/workflow`,
  );

const transitionDirectoryValueWorkflow = (
  directoryId: number,
  valueId: number,
  params: TransitionDirectoryWorkflowParams,
) =>
  ApiProjects2.post<
    string,
    Promise<AxiosResponse<TransitionDirectoryWorkflowRes>>,
    TransitionDirectoryWorkflowParams
  >(`/directory/${directoryId}/value/${valueId}/workflow/transition`, params);

const getDirectoryValueWorkflowHistory = (
  directoryId: number,
  valueId: number,
  params?: GetDirectoryWorkflowHistoryParams,
) => {
  const query = new URLSearchParams();
  if (typeof params?.limit === 'number') {
    query.set('limit', String(params.limit));
  }
  if (typeof params?.offset === 'number') {
    query.set('offset', String(params.offset));
  }
  const queryString = query.toString();
  return ApiProjects2.get<void, Promise<AxiosResponse<GetDirectoryWorkflowHistoryRes>>>(
    `/directory/${directoryId}/value/${valueId}/workflow/history${
      queryString ? `?${queryString}` : ''
    }`,
  );
};

const getProjectDirectories = (projectId: number) =>
  ApiProjects2.get<void, Promise<AxiosResponse<ListProjectDirectoriesRes>>>(
    `/project/${projectId}/directories`,
  );

const getDirectoryAttributes = (directoryId: number) =>
  ApiProjects2.get<void, Promise<AxiosResponse<ListDirectoryAttributesRes>>>(
    `/directory/${directoryId}/attributes`,
  );

const createDirectoryAttribute = (directoryId: number, payload: DirectoryAttributePayload) =>
  ApiProjects2.post<
    string,
    Promise<AxiosResponse<ListDirectoryAttributesRes>>,
    DirectoryAttributePayload
  >(`/directory/${directoryId}/attributes`, payload);

const updateDirectoryAttribute = (
  directoryId: number,
  attributeId: number,
  payload: UpdateDirectoryAttributePayload,
) =>
  ApiProjects2.put<
    string,
    Promise<AxiosResponse<ListDirectoryAttributesRes>>,
    UpdateDirectoryAttributePayload
  >(`/directory/${directoryId}/attributes/${attributeId}`, payload);

const deleteDirectoryAttribute = (directoryId: number, attributeId: number) =>
  ApiProjects2.delete<string, Promise<AxiosResponse<{ directoryId: number; deletedId: number }>>>(
    `/directory/${directoryId}/attributes/${attributeId}`,
  );

const getDirectoryHierarchy = (directoryId: number) =>
  ApiProjects2.get<void, Promise<AxiosResponse<ListDirectoryHierarchyRes>>>(
    `/directory/${directoryId}/hierarchy`,
  );

const createDirectoryHierarchyItem = (directoryId: number, payload: DirectoryHierarchyPayload) =>
  ApiProjects2.post<
    string,
    Promise<AxiosResponse<ListDirectoryHierarchyRes>>,
    DirectoryHierarchyPayload
  >(`/directory/${directoryId}/hierarchy`, payload);

const updateDirectoryHierarchyItem = (
  directoryId: number,
  itemId: number,
  payload: UpdateDirectoryHierarchyPayload,
) =>
  ApiProjects2.put<
    string,
    Promise<AxiosResponse<ListDirectoryHierarchyRes>>,
    UpdateDirectoryHierarchyPayload
  >(`/directory/${directoryId}/hierarchy/${itemId}`, payload);

const deleteDirectoryHierarchyItem = (directoryId: number, itemId: number) =>
  ApiProjects2.delete<string, Promise<AxiosResponse<{ directoryId: number; deletedId: number }>>>(
    `/directory/${directoryId}/hierarchy/${itemId}`,
  );

const upsertFormDirectoryBinding = (formId: number, payload: UpsertFormDirectoryBindingPayload) =>
  ApiProjects2.put<string, Promise<AxiosResponse<any>>, UpsertFormDirectoryBindingPayload>(
    `/form/${formId}/reference-binding`,
    payload,
  );

const ProjectsService = {
  getProjects,
  getProjectsWithSearch,
  getFormsByProjectId,
  createProject,
  createForm,
  createFormsBulk,
  previewFormsBulk,
  getTableSettings,
  updateForm,
  addColumnForm,
  deleteColumnForm,
  updateColumnForm,
  getFormWorkflow,
  upsertFormWorkflow,
  transitionFormWorkflow,
  getFormWorkflowHistory,
  getDirectoryWorkflow,
  upsertDirectoryWorkflow,
  getDirectoryValueWorkflow,
  transitionDirectoryValueWorkflow,
  getDirectoryValueWorkflowHistory,
  getProjectDirectories,
  getDirectoryAttributes,
  createDirectoryAttribute,
  updateDirectoryAttribute,
  deleteDirectoryAttribute,
  getDirectoryHierarchy,
  createDirectoryHierarchyItem,
  updateDirectoryHierarchyItem,
  deleteDirectoryHierarchyItem,
  upsertFormDirectoryBinding,
};

export default ProjectsService;
